import type {
  ClientExceptionEntity,
  ClientExceptionEntityTypeEnum,
  Middleware
} from '@DigitaleDoerfer/digitale-doerfer-api';

export class ClientExceptionError
  extends Error
  implements ClientExceptionEntity
{
  // ClientExceptionEntity fields
  detail?: string;
  error?: string;
  exception?: string;
  path?: string;
  status?: string;
  timestamp?: number;
  type?: ClientExceptionEntityTypeEnum;
  // Extra metadata
  httpStatus: number;

  constructor(entity: ClientExceptionEntity, httpStatus: number) {
    super(entity.message || entity.error || `HTTP ${httpStatus}`);
    Object.setPrototypeOf(this, new.target.prototype); // fix prototype chain
    this.name = 'ClientExceptionError';

    // Assign entity fields
    this.detail = entity.detail;
    this.error = entity.error;
    this.exception = entity.exception;
    this.path = entity.path;
    this.status = entity.status ?? String(httpStatus);
    this.timestamp = entity.timestamp ?? Date.now();
    this.type = entity.type;

    // Extra metadata
    this.httpStatus = httpStatus;
  }
}

const isClientExceptionEntity = (
  entity: unknown
): entity is ClientExceptionEntity => {
  if (!entity || typeof entity !== 'object') return false;
  const o = entity as Record<string, unknown>;
  return (
    'message' in o ||
    'error' in o ||
    'type' in o ||
    'status' in o ||
    'timestamp' in o ||
    'path' in o ||
    'detail' in o ||
    'exception' in o
  );
};

export const errorMiddleware: Middleware = {
  async post(context) {
    const { response } = context;
    if (response.ok) return response;

    // Try to parse body safely
    let parsed: unknown;
    try {
      const contentType = response.headers.get('content-type') || '';
      parsed = contentType.includes('json')
        ? await response.clone().json()
        : await response.clone().text();
    } catch {
      // ignore parse failures
    }

    let entity: ClientExceptionEntity;
    if (isClientExceptionEntity(parsed)) {
      entity = parsed;
    } else {
      entity = {
        message: `HTTP ${response.status} ${response.statusText}`,
        error: response.statusText || undefined,
        status: String(response.status),
        timestamp: Date.now()
      };
    }

    throw new ClientExceptionError(entity, response.status);
  }
};

export const getClientErrorType = (
  error: unknown
): ClientExceptionEntityTypeEnum | undefined =>
  error instanceof ClientExceptionError ? error.type : undefined;
